import { fromE164ToLocalFrench, toE164French } from './phone.js';

/**
 * Pré-remplissage de la création de fiche par lien (19/10/2026, demande de
 * Bolting relayée par Cyrille) : le logiciel du réseau ouvre
 *
 *   https://doorly.pro/leads/new#nom=DUPONT&prenom=Alain&telephone=0623070207&cp=34000
 *
 * et le vendeur retrouve son client déjà saisi — pas de double saisie, et
 * aucune API Doorly exposée au réseau (le vendeur reste connecté, valide,
 * passe le consentement SMS comme d'habitude). Le FRAGMENT (`#…`) est
 * recommandé : il n'est jamais envoyé au serveur, donc absent des journaux
 * d'accès. `?…` est aussi lu.
 *
 * Fonction pure (pas de DOM) : le front l'appelle sur `location`.
 */

export interface LeadPrefill {
  civilite?: 'M.' | 'Mme';
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  address?: string;
  postalCode?: string;
  city?: string;
}

/** Clés acceptées (français d'abord, alias anglais), insensibles à la casse. */
const KEY_ALIASES: Record<string, keyof LeadPrefill> = {
  civilite: 'civilite',
  'civilité': 'civilite',
  civility: 'civilite',
  nom: 'lastName',
  lastname: 'lastName',
  last_name: 'lastName',
  prenom: 'firstName',
  'prénom': 'firstName',
  firstname: 'firstName',
  first_name: 'firstName',
  telephone: 'phone',
  'téléphone': 'phone',
  tel: 'phone',
  phone: 'phone',
  mobile: 'phone',
  email: 'email',
  mail: 'email',
  adresse: 'address',
  address: 'address',
  cp: 'postalCode',
  code_postal: 'postalCode',
  codepostal: 'postalCode',
  postalcode: 'postalCode',
  postal_code: 'postalCode',
  ville: 'city',
  city: 'city',
};

/** Clés du lien que Doorly lit (pour les retirer de la barre d'adresse). */
export function isLeadPrefillKey(key: string): boolean {
  return key.trim().toLowerCase() in KEY_ALIASES;
}

const MAX_LENGTH = 200;

function civiliteOf(raw: string): LeadPrefill['civilite'] | undefined {
  const v = raw.trim().toLowerCase().replace(/\.$/, '');
  if (['m', 'mr', 'monsieur', 'homme', 'h'].includes(v)) return 'M.';
  if (['mme', 'madame', 'mlle', 'mademoiselle', 'femme', 'f'].includes(v)) return 'Mme';
  return undefined;
}

/**
 * Lit les champs connus d'une chaîne `clé=valeur&…` (fragment ou query, avec
 * ou sans `#`/`?` initial). Champs inconnus ignorés, valeurs vides ignorées,
 * longueur bornée. Téléphone ramené au format affiché par le formulaire
 * (`06 23 07 02 07`), quel que soit le format reçu.
 */
export function parseLeadPrefill(raw: string | null | undefined): LeadPrefill {
  const out: LeadPrefill = {};
  const query = (raw ?? '').replace(/^[#?]/, '');
  if (!query.includes('=')) return out;
  const params = new URLSearchParams(query);
  for (const [k, v] of params) {
    const field = KEY_ALIASES[k.trim().toLowerCase()];
    const value = v.trim().slice(0, MAX_LENGTH);
    if (!field || !value) continue;
    switch (field) {
      case 'civilite': {
        const c = civiliteOf(value);
        if (c) out.civilite = c;
        break;
      }
      case 'phone': {
        const local = fromE164ToLocalFrench(toE164French(value)).replace(/\D/g, '');
        const formatted = local.slice(0, 10).replace(/(\d{2})(?=\d)/g, '$1 ').trim();
        if (formatted) out.phone = formatted;
        break;
      }
      case 'email':
        out.email = value.toLowerCase();
        break;
      default:
        out[field] = value;
    }
  }
  return out;
}

/** Le lien porte-t-il au moins un champ connu ? */
export function hasLeadPrefill(prefill: LeadPrefill): boolean {
  return Object.keys(prefill).length > 0;
}
